import { useTranslation } from 'react-i18next';
import { Card, Stack, Text } from '@mantine/core';
import {
  IconBuildingWarehouse,
  IconClipboardCheck,
  IconFileText,
  IconMessage,
  IconScale,
  IconTruckLoading,
} from '@tabler/icons-react';
import SectionLabel from '@/components/common/SectionLabel';
import TermRow from '@/components/common/TermRow';
import LoadingConditionsSummary from '@/components/app/sell/LoadingConditionsSummary';
import type { SaleOrder } from '@/types/sell';

const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.sell.terms.creditAverage',
  BY_EACH_DAY: 'app.sell.terms.creditEachDay',
};

// «Условия предложения» для оффера продажи: зачёт качества → приёмка → условия погрузки → хранение
// → декларация → комментарий. Данные — из самого оффера (getSaleOffer), доп. запросов нет.
export default function SaleOfferTerms({ offer }: { readonly offer: SaleOrder }) {
  const { t } = useTranslation();
  const creditKey = offer.qualityCreditType ? CREDIT_TYPE_KEYS[offer.qualityCreditType] : undefined;
  const lc = offer.loadingConditions;

  return (
    <Stack gap="sm">
      <SectionLabel>{t('app.sell.terms.title')}</SectionLabel>
      <Card withBorder padding="lg">
        <Stack gap="md">
          {offer.qualityCreditType ? (
            <TermRow icon={<IconScale size={18} />} label={t('app.sell.terms.credit')}>
              <Text size="sm" c="gray.8">
                {creditKey ? t(creditKey) : offer.qualityCreditType}
              </Text>
            </TermRow>
          ) : null}

          <TermRow icon={<IconClipboardCheck size={18} />} label={t('app.sell.terms.acceptance')}>
            <Text size="sm" c="gray.8">
              {offer.onlyByGOST ? t('app.sell.terms.acceptanceGostOnly') : t('app.sell.terms.acceptanceFlexible')}
            </Text>
          </TermRow>

          {lc ? (
            <TermRow icon={<IconTruckLoading size={18} />} label={t('app.sell.loading.title')}>
              <LoadingConditionsSummary lc={lc} />
            </TermRow>
          ) : null}

          {lc ? (
            <TermRow icon={<IconBuildingWarehouse size={18} />} label={t('app.sell.terms.storage')}>
              <Text size="sm" c="gray.8">
                {lc.storageAvailable
                  ? t('app.sell.terms.storageAvailable', { price: lc.storagePrice ?? 0 })
                  : t('app.sell.terms.storageUrgent')}
              </Text>
            </TermRow>
          ) : null}

          {offer.declarationNo ? (
            <TermRow icon={<IconFileText size={18} />} label={t('app.sell.terms.declaration')}>
              <Text size="sm" c="gray.8">
                № {offer.declarationNo}
                {offer.declarationDate ? ` · ${offer.declarationDate}` : ''}
              </Text>
            </TermRow>
          ) : null}

          {offer.userComment ? (
            <TermRow icon={<IconMessage size={18} />} label={t('app.sell.terms.comment')}>
              <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>
                {offer.userComment}
              </Text>
            </TermRow>
          ) : null}
        </Stack>
      </Card>
    </Stack>
  );
}
